/**
 * 登录页（P-01，路由 /login）。
 *
 * 设计约束（docs/03-UIUX.md §5 P-01）：
 * - 单列表单（邮箱 / 密码），触摸目标 >= 44×44px；
 * - 失败**不区分**「用户不存在」与「密码错误」，统一文案「邮箱或密码不正确」；
 * - 连续 5 次失败显示限流倒计时（服务端 9004，LoginPolicy 15 分钟锁定）；
 * - 密码框支持密码管理器自动填充（name 用 email/password，autocomplete 配对）。
 *
 * CSRF：挂载时预取一次令牌（endpoints.getCsrfToken），避免用户点登录时多等一个 RTT；
 * 变更请求路径上的自动获取由 client.ensureCsrfToken 兜底。
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, TRANSPORT_CODE } from '../api/client';
import { getCsrfToken } from '../api/endpoints';
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, EMAIL_MAX_LENGTH } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';

const UNIFIED_ERROR = '邮箱或密码不正确';

/** 限流冷却（秒）。服务端未给 Retry-After 时的兜底估算 */
const RATE_LIMIT_DEFAULT_SECONDS = 15 * 60;

export function LoginPage() {
  const { login: doLogin, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number | null>(null);

  // 登录成功或已登录 → 跳回 next（默认首页）
  const nextPath = new URLSearchParams(location.search).get('next') ?? '/';
  const navigatedRef = useRef(false);
  useEffect(() => {
    if (currentUser && !navigatedRef.current) {
      navigatedRef.current = true;
      navigate(nextPath, { replace: true });
    }
  }, [currentUser, nextPath, navigate]);

  // 挂载时预取 CSRF；失败不阻塞（真实请求路径会重试）
  useEffect(() => {
    void getCsrfToken().catch(() => undefined);
  }, []);

  // 限流倒计时
  useEffect(() => {
    if (rateLimitSeconds === null || rateLimitSeconds <= 0) {
      if (rateLimitSeconds === 0) setRateLimitSeconds(null);
      return undefined;
    }
    const timer = globalThis.setTimeout(() => {
      setRateLimitSeconds((s) => (s === null ? null : Math.max(0, s - 1)));
    }, 1000);
    return () => globalThis.clearTimeout(timer);
  }, [rateLimitSeconds]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting || rateLimitSeconds !== null) return;

    setSubmitting(true);
    setError(null);
    try {
      await doLogin({ email: email.trim(), password });
      // 登录成功后的跳转由上方 currentUser effect 处理
    } catch (err) {
      if (err instanceof ApiError && err.isRateLimited) {
        setRateLimitSeconds(err.retryAfterSeconds ?? RATE_LIMIT_DEFAULT_SECONDS);
        setError('登录尝试过于频繁，请稍后再试。');
      } else if (err instanceof ApiError && err.isUnauthorized) {
        setError(UNIFIED_ERROR);
      } else if (err instanceof ApiError && err.code === TRANSPORT_CODE) {
        setError('无法连接服务器，请确认后端已启动后重试。');
      } else {
        setError(UNIFIED_ERROR);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <div className="login-brand">
          <Icon name="shield-check" size={ICON_SIZE.standalone} label="LicenseHarbor" />
          <h1 className="login-title">LicenseHarbor</h1>
          <p className="login-subtitle">自托管软件授权管理</p>
        </div>

        <div className="field">
          <label htmlFor="login-email">邮箱</label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={EMAIL_MAX_LENGTH}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting || rateLimitSeconds !== null}
          />
        </div>

        <div className="field">
          <label htmlFor="login-password">密码</label>
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            maxLength={PASSWORD_MAX_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting || rateLimitSeconds !== null}
          />
        </div>

        {error !== null ? (
          <p className="login-error" role="alert">
            <Icon name="octagon-x" size={ICON_SIZE.inline} />
            <span>
              {error}
              {rateLimitSeconds !== null
                ? `（${Math.ceil(rateLimitSeconds / 60)} 分钟后可重试）`
                : ''}
            </span>
          </p>
        ) : null}

        <button
          type="submit"
          className="btn btn--primary login-submit"
          disabled={submitting || rateLimitSeconds !== null}
        >
          {submitting ? (
            <>
              <Icon name="loader-circle" size={ICON_SIZE.inline} spin />
              <span>登录中…</span>
            </>
          ) : (
            '登录'
          )}
        </button>
      </form>
    </main>
  );
}
